import { Controller } from '@hotwired/stimulus';
import {
  ezDebounce,
  getWindowWidth,
  addListenerMulti,
  removeListenerMulti,
} from 'lib/helpers';
import MediaMatcher from 'lib/media-matcher';
import { EZ_CAROUSEL_TRIGGER_REINIT } from 'lib/ez-custom-events';

const mediaMatcher = new MediaMatcher();

export default class EzCarouselController extends Controller {
  static targets= [
    'outerWrap',
    'innerWrap',
    'slide',
    'arrowLeft',
    'arrowRight',
  ]

  connect() {
    this.options = {
      transition: {
        speed: this.element.dataset.speed || 500,
        easing: '',
      },
      swipe: this.allowSwipe(),
      afterChangeSlide: function afterChangeSlide() { /* do nothing */ },
      numSlidesMove: {
        largeScreen: parseInt(this.element.dataset.perSlideLarge, 10) || 6,
        mediumScreen: parseInt(this.element.dataset.perSlideMedium, 10) || 3,
        smallScreen: parseInt(this.element.dataset.perSlideSmall, 10) || 1,
      },
      mobileBreakpoint: this.element.dataset.mobileBreakpoint || null,
      mobileDisable: this.element.dataset.mobileDisable || false,
    };

    this.arrows = {
      left: this.arrowLeftTarget,
      right: this.arrowRightTarget,
    };

    if (this.options.mobileBreakpoint) {
      const mobileBreakpoint = parseInt(this.options.mobileBreakpoint, 10) + 1;

      mediaMatcher.addMediaQuery(`(min-width: ${mobileBreakpoint}px)`,
        () => { this.initCarousel(); },
        () => { this.initCarousel(); });

      mediaMatcher.evaluateAll();
      mediaMatcher.addListeners();
    }

    this.initCarousel();

    window.addEventListener(EZ_CAROUSEL_TRIGGER_REINIT, this.initCarousel);
  }

  disconnect() {
    if (this.options.mobileDisable) {
      mediaMatcher.removeListeners();
      mediaMatcher.clearQueries();
    }

    window.removeEventListener(EZ_CAROUSEL_TRIGGER_REINIT, this.initCarousel);
  }

  allowSwipe = () => !this.element.dataset.disallowSwipe

  initCarousel = () => {
    // Set some initial values
    this.innerWrapTarget.style.transition = `left ${this.options.transition.speed / 1000}s ${this.options.transition.easing}`;
    this.curPos = 0;
    this.totalSlides = this.slideTargets.length;
    this.isAnimating = false;

    // Set up responsive behaviors
    window.addEventListener('resize', ezDebounce(() => {
      this.setupResponsive();
    }), 500);
    this.setupResponsive();

    // Set main slider width
    this.innerWrapTarget.style.width = `${this.calculateSlidesWidth() + 20}px`;

    // Set up swiping behaviors
    if (this.options.swipe) {
      addListenerMulti(this.innerWrapTarget, 'mousedown touchstart', this.startSwipe);
    }
  }

  calculateSlidesWidth(slidesArr = this.slideTargets) {
    let totalWidth = 0;

    for (let slide = 0, totalSlides = slidesArr.length;
      slide < totalSlides; slide += 1) {
      const slideWidth = this.findSlideWidth(slidesArr[slide]);

      totalWidth += Math.ceil(parseInt(slideWidth, 10)) + 10;
    }

    return totalWidth;
  }

  findSlideWidth(slide) {
    return slide.getAttribute(this.getSlideWidthAttr()) || slide.offsetWidth;
  }

  getSlideWidthAttr() {
    if (this.options.mobileBreakpoint && getWindowWidth() <= this.options.mobileBreakpoint) {
      return 'data-thumb-width-mobile';
    }

    return 'data-thumb-width';
  }

  determineArrowVisibility(outerWrap = this.outerWrapTarget, innerWrap = this.innerWrapTarget) {
    if (innerWrap.offsetWidth <= outerWrap.offsetWidth) {
      this.arrows.right.classList.remove('is-active');
    } else {
      this.arrows.right.classList.add('is-active');
    }
  }

  arrowClick(event) {
    const dir = event.currentTarget.dataset.direction;

    if (!this.element.classList.contains('is-animating')) {
      if (this.getCurEdge(dir) < 0) {
        setTimeout(() => {
          this.move(dir);
        }, 20);
      }
    }
  }

  setupResponsive() {
    this.isMobile = false;

    this.numSlidesMove = this.options.numSlidesMove.smallScreen; // offset for lowest res

    if (getWindowWidth() > 800) {
      this.numSlidesMove = this.options.numSlidesMove.largeScreen; // offset for highest res
    } else {
      if (getWindowWidth() > 450) {
        this.numSlidesMove = this.options.numSlidesMove.mediumScreen; // offset for middle res
      }

      this.isMobile = true;
    }

    this.resetCarousel();
  }

  resetCarousel() {
    this.innerWrapTarget.style.left = '0px';
    this.curPos = 0;

    this.arrows.left.classList.remove('is-active');

    // Check widths to determine arrow visibility
    this.determineArrowVisibility();
  }

  getOppositeDir(dir) {
    let oppositeDir;

    if (dir === 'left') {
      oppositeDir = 'right';
    } else {
      oppositeDir = 'left';
    }

    return oppositeDir;
  }

  getCurEdge(dir) {
    const boundsAdjustment = -3;
    let curEdge;

    if (dir === 'left') {
      curEdge = this.innerWrapTarget.offsetLeft;
    } else {
      curEdge = this.element.offsetWidth - (this.innerWrapTarget.offsetLeft + this.innerWrapTarget.offsetWidth);
    }

    if (curEdge < 0 && curEdge > boundsAdjustment) {
      this.arrows[dir].classList.remove('is-active');
    }

    return curEdge;
  }

  move(dir) {
    const transitionAmt = this.calcTransition(dir);

    this.innerWrapTarget.style.left = `${this.getCurEdge('left') + transitionAmt}px`;

    this.element.classList.add('is-animating');

    if (this.getCurEdge(dir) < 0) {
      this.arrows[this.getOppositeDir(dir)].classList.add('is-active');
    }

    setTimeout(() => {
      this.element.classList.remove('is-animating');

      if (this.getCurEdge(dir) >= 0) {
        this.arrows[dir].classList.remove('is-active');
      }

      this.curPos = this.nextPos;
    }, this.options.transition.speed);

    this.options.afterChangeSlide(this);
  }

  calcTransition(dir) {
    const movingItems = this.getSlidesSubArray(dir);

    let transitionAmt = this.calculateSlidesWidth(movingItems);

    if (this.predictNextEdge(dir, transitionAmt) < 0) {
      if (dir === 'left') {
        transitionAmt = -this.getCurEdge(dir);
      } else {
        transitionAmt = this.getCurEdge(dir);
      }
    } else if (dir === 'right') {
      transitionAmt *= -1;
    }

    return transitionAmt;
  }

  predictNextEdge(dir, transitionAmt) {
    return Math.abs(this.getCurEdge(dir)) - transitionAmt;
  }

  getSlidesSubArray(dir) {
    let subArray;

    if (dir === 'left') {
      this.nextPos = this.curPos - this.numSlidesMove;

      subArray = this.slideTargets.slice(this.nextPos, (this.nextPos + this.numSlidesMove));
    } else {
      this.nextPos = this.curPos + this.numSlidesMove;

      subArray = this.slideTargets.slice(this.curPos, this.nextPos);
    }
    return subArray;
  }

  startSwipe = (e) => {
    let touch = e;

    this.getCurEdge('left');

    if (!this.isAnimating) {
      if (e.type === 'touchstart') {
        touch = e.targetTouches[0] || e.changedTouches[0];
      }

      this.startX = touch.pageX;
      this.startY = touch.pageY;

      addListenerMulti(this.innerWrapTarget, 'mousemove touchmove', this.swipeMove);
      addListenerMulti(document.body, 'mouseup touchend', this.swipeEnd);
    }
  }

  swipeMove = (e) => {
    let touch = e;

    if (e.type === 'touchmove') {
      touch = e.targetTouches[0] || e.changedTouches[0];
    }

    this.moveX = touch.pageX;
    this.moveY = touch.pageY;

    // for scrolling up and down
    if (Math.abs(this.moveX - this.startX) < 40) return;

    this.isAnimating = true;

    this.element.classList.add('is-animating');

    const dir = this.startX < this.moveX ? 'left' : 'right';

    this.move(dir);
  }

  swipeEnd = () => {
    if (Math.abs(this.moveX - this.startX) === 0) return;

    delete this.startX;
    delete this.startY;
    delete this.moveX;
    delete this.moveY;

    this.isAnimating = false;

    this.element.classList.remove('is-animating');

    removeListenerMulti(this.innerWrapTarget, 'mousemove touchmove', this.swipeMove);
    removeListenerMulti(document.body, 'mouseup touchend', this.swipeEnd);
  }
}
